import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { EndpointService, type ID } from "@/types/api/api";
import type { IAPI } from "./APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";

export interface IListSlotsParams {
  search?: string;
  slot_type?: number;
  slot_category_id?: number;
  isVenueSpecific?: boolean;
}

export interface ISlot {
  last_updated: string;
  session_id: ID;
  max_number_of_heads: number;
  number_of_heads: number;
  id: number;
  slot_type: number;
  name: string;
  session_status: number;
  status: number;
  slot_category_id: null | number;
  number: number;
  visibility: number;
}

export interface IListSlotCategoriesParams {
  search?: string;
  isVenueSpecific?: boolean;
}

export interface ISlotCategory {
  id: number;
  name: string;
  position: number;
}

export interface ITableLocationService {
  listSlots: (params?: IListSlotsParams) => UseQueryResult<Array<ISlot>, Error>;
  dataSourceListQuery: (
    params: IListSlotsParams,
  ) => Promise<AxiosResponse<Array<ISlot>>>;

  listSlotCategories: (
    params?: IListSlotCategoriesParams,
  ) => UseQueryResult<Array<ISlotCategory>, Error>;
  dataSourceListQuerySlotCategories: (
    params: IListSlotCategoriesParams,
  ) => Promise<AxiosResponse<Array<ISlotCategory>>>;
}

export class TableLocationService implements ITableLocationService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  // Slots
  listSlots(params: IListSlotsParams): UseQueryResult<Array<ISlot>, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["table-locations", "list-slots"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { isVenueSpecific = false, ...restParams } = updatedParams;
        const { data } = await this.api.get<AxiosResponse<Array<ISlot>>>(
          "/slots/",
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
          },
        );

        return data;
      },
    });
  }

  async dataSourceListQuery(
    params: IListSlotsParams,
  ): Promise<AxiosResponse<Array<ISlot>>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );

    return this.api.get<AxiosResponse<Array<ISlot>>>("/slots/", {
      params: updatedParams,
      endpointService: EndpointService.Gateway,
    });
  }

  // Slot Categories
  listSlotCategories(
    params?: IListSlotCategoriesParams,
  ): UseQueryResult<Array<ISlotCategory>, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["table-locations", "list-slot-categories"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { isVenueSpecific = false } = updatedParams;
        const { data } = await this.api.get<
          AxiosResponse<Array<ISlotCategory>>
        >("/slot_categories/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
        });

        return data;
      },
    });
  }

  async dataSourceListQuerySlotCategories(
    params: IListSlotCategoriesParams,
  ): Promise<AxiosResponse<Array<ISlotCategory>>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );

    return this.api.get<AxiosResponse<Array<ISlotCategory>>>(
      "/slot_categories/",
      {
        params: updatedParams,
        endpointService: EndpointService.Gateway,
      },
    );
  }
}
